import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, PLATFORM_ID } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faUserCircle,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import { SharedUserService } from '../../shared/services/shared/user/shared-user.service';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NavbarControlComponent } from '../../shared/controls/navbar-control/navbar-control.component';
import { ContactUsSubmissionService } from '../../shared/services/transactional/contact-us-submission/contact-us-submission.service';
import { ICreateContactUsSubmission } from '../../shared/models/dtos/create/create-contact-us-submission.interface';
import { finalize } from 'rxjs';
import { SpinnerService } from '../../shared/services/shared/spinner/spinner.service';
import { ToastService } from '../../shared/services/shared/toast/toast.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { loadComponentTranslations } from '../../shared/funcs/lang-loader';

@Component({
  selector: 'app-contact-us',
  imports: [
    FontAwesomeModule,
    TranslateModule,
    RouterModule,
    NgbDropdownModule,
    ReactiveFormsModule,
    NavbarControlComponent,
  ],
  templateUrl: './contact-us.component.html',
  styleUrl: './contact-us.component.scss',
})
export class ContactUsComponent {
  public faUserCircle: IconDefinition = faUserCircle;
  public isLoggedIn: boolean = false;

  public contactForm: FormGroup = new FormGroup({
    name: new FormControl<string | undefined>(undefined, [Validators.required]),
    email: new FormControl<string | undefined>(undefined, [
      Validators.required,
      Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
    ]),
    phone: new FormControl<string | undefined>(undefined, [
      Validators.required,
    ]),
    subject: new FormControl<string | undefined>(undefined, [
      Validators.required,
    ]),
    message: new FormControl<string | undefined>(undefined, [
      Validators.required,
    ]),
  });

  constructor(
    private titleService: Title,
    private metaService: Meta,
    private _spinnerService: SpinnerService,
    private _toastService: ToastService,
    private _translateService: TranslateService,
    @Inject(PLATFORM_ID) private platformId: Object,

    private _http: HttpClient,
    private _contactUsSubmissionService: ContactUsSubmissionService,
  ) {}

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  public ngOnInit(): void {
    loadComponentTranslations(
      this._http,
      this._translateService,
      'components/contact-us',
      this.isBrowser(),
    ).subscribe({
      next: () => {},
      error: () => {},
    });

    this.titleService.setTitle('ClockFlex - Contact us');

    // Set Meta Tags for SEO
    this.metaService.updateTag({
      name: 'description',
      content:
        'Get in touch with ClockFlex for inquiries, support, or to learn more about our time tracking and team collaboration software. We are here to assist you!',
    });
    this.metaService.updateTag({
      name: 'robots',
      content: 'index, follow',
    });
    this.metaService.updateTag({
      property: 'og:title',
      content: 'Contact Us - ClockFlex',
    });
    this.metaService.updateTag({
      property: 'og:description',
      content:
        'Get in touch with ClockFlex for inquiries, support, or to learn more about our time tracking and team collaboration software. We are here to assist you!',
    });

    this.metaService.updateTag({
      property: 'og:url',
      content: 'https://www.clockflex.com/contact-us',
    });
    this.metaService.updateTag({
      name: 'twitter:title',
      content: 'Contact Us - ClockFlex',
    });
    this.metaService.updateTag({
      name: 'twitter:description',
      content:
        'Get in touch with ClockFlex for inquiries, support, or to learn more about our time tracking and team collaboration software. We are here to assist you!',
    });
    this.metaService.updateTag({
      name: 'twitter:image',
      content: './assets/images/contact-us-image.jpg', // Replace with your contact image path
    });
    this.metaService.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });
  }

  public onSubmit(): void {
    if (!this.contactForm.valid) {
      this.contactForm.markAllAsTouched();
      return;
    }
    this._spinnerService.show('Submitting your request...');
    let model: ICreateContactUsSubmission = {
      email: this.contactForm.get('email')?.value,
      phone: this.contactForm.get('phone')?.value,
      message: this.contactForm.get('message')?.value,
      subject: this.contactForm.get('subject')?.value,
      name: this.contactForm.get('name')?.value,
    };
    this._contactUsSubmissionService
      .create(model)
      .pipe(
        finalize(() => {
          this._spinnerService.hide();
        }),
      )
      .subscribe({
        next: (response) => {
          if (response.isSuccess) {
            if (response.data === true) {
              this._toastService.showSuccess(
                "Yor request of a query is submitted. we'll contact you soon.",
              );
            }
          }
        },
      });
  }
}
